import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { MdDelete, MdEdit } from "react-icons/md";
import { XMarkIcon, CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { FiPlus } from "react-icons/fi";
import { useConstructionCalculatorHook } from "../useHook";
import Loader, { ImageWithFallback } from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import SimilarItemsModal from "../../../Components/Modals/SimilarItems/SimilarItems";
import { MdLink } from "react-icons/md";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

function ConstructionCalculator() {
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission(
    SystemPermissions.ConstructionCalculators.UPDATE,
  );
  const canSimilar = hasPermission(
    SystemPermissions.ConstructionCalculators.SIMILAR,
  );
  const canAdd = hasPermission(
    SystemPermissions.ConstructionCalculators.CREATE,
  );
  const {
    getConstructionCalculators,
    addConstructionCalculator,
    updateConstructionCalculator,
    deleteConstructionCalculator,
  } = useConstructionCalculatorHook();

  const [calculators, setCalculators] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mainLoading, setMainLoading] = useState(true);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedCalcForSimilar, setSelectedCalcForSimilar] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    offset: parseInt(searchParams.get("page") || "0") * 10,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [formData, setFormData] = useState({
    file: null,
    name: "",
    custom_url: "",
    preview: null,
  });

  useEffect(() => {
    fetchCalculators();
  }, [pagination.offset, pagination.limit]);

  const fetchCalculators = async () => {
    try {
      setMainLoading(true);
      await getConstructionCalculators(
        setCalculators,
        setPagination,
        pagination.offset,
        pagination.limit,
      );
    } catch (error) {
      console.error("Error fetching calculators:", error);
    } finally {
      setMainLoading(false);
    }
  };

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / pagination.limit);
    setSearchParams({ page: newPage.toString() });
    setPagination((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const openModal = (index = null) => {
    if (index !== null && calculators[index]) {
      const calcToEdit = calculators[index];
      setFormData({
        file: null,
        name: calcToEdit.name,
        custom_url: calcToEdit.custom_url,
        preview: calcToEdit.image_url, // This should be the full URL from backend getter
      });
      setEditIndex(index);
    } else {
      setFormData({ file: null, name: "", custom_url: "", preview: null });
      setEditIndex(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (formData.preview && formData.preview.startsWith("blob:")) {
      URL.revokeObjectURL(formData.preview);
    }
    setIsModalOpen(false);
    setEditIndex(null);
    setSubmitting(false);
    setFormData({ file: null, name: "", custom_url: "", preview: null });
  };

  const isFormValid = () => {
    if (editIndex !== null) {
      return formData.name.trim().length > 0;
    }
    return (
      formData.name.trim().length > 0 && formData.custom_url.trim().length > 0
    );
  };

  const handleAddCalculator = async () => {
    try {
      setSubmitting(true);
      const fd = new FormData();
      if (formData.file) {
        fd.append("image", formData.file);
      }
      fd.append("name", formData.name.trim());
      fd.append("custom_url", formData.custom_url.trim());

      await addConstructionCalculator(fd);
      Swal.fire("Success!", "Calculator added successfully.", "success");
      await fetchCalculators();
      closeModal();
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCalculator = async () => {
    try {
      setSubmitting(true);
      const calcId = calculators[editIndex].id;
      const fd = new FormData();
      if (formData.file) {
        fd.append("image", formData.file);
      }
      fd.append("name", formData.name.trim());

      await updateConstructionCalculator(calcId, fd);
      Swal.fire("Success!", "Calculator updated successfully.", "success");
      await fetchCalculators();
      closeModal();
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCalculator = (index) => {
    const calcId = calculators[index].id;
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setDeletingIndex(index);
          await deleteConstructionCalculator(calcId);
          Swal.fire("Deleted!", "Calculator has been deleted.", "success");
          await fetchCalculators();
        } catch (error) {
          Swal.fire("Error!", error.message, "error");
        } finally {
          setDeletingIndex(null);
        }
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setFormData({ ...formData, file, preview });
    }
  };

  const removePreviewImage = () => {
    if (formData.preview && formData.preview.startsWith("blob:")) {
      URL.revokeObjectURL(formData.preview);
    }
    setFormData({ ...formData, file: null, preview: null });
  };

  return (
    <div className="p-4 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Construction Calculators
        </h2>
        {/*  {(pagination?.totalCount ?? calculators.length) < 7 && canAdd && (
          <button
            onClick={() => openModal(null)}
            className="btn btn-primary flex items-center gap-2"
          >
            <FiPlus /> Add Calculator
          </button>
        )} */}
      </div>

      {mainLoading ? (
        <Loader message="Loading calculators..." />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="brand-gradient text-white">
                  <th className="table-th text-left">Sr. No.</th>
                  <th className="table-th text-left">Image</th>
                  <th className="table-th text-left">Name</th>
                  <th className="table-th text-left">Custom URL</th>
                  {(canUpdate || canSimilar) && (
                    <th className="table-th text-center">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {calculators.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canUpdate || canSimilar ? 5 : 4}
                      className="text-center py-10 text-gray-500"
                    >
                      No calculators found.
                    </td>
                  </tr>
                ) : (
                  calculators.map((calc, index) => (
                    <tr
                      key={calc.id}
                      className={`${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="table-td">
                        {pagination.offset + index + 1}
                      </td>
                      <td className="table-td">
                        <div className="w-16 h-12 rounded overflow-hidden border border-gray-200 bg-gray-50">
                          <ImageWithFallback
                            src={calc.image_url}
                            alt={calc.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </td>
                      <td className="table-td font-medium text-gray-800">
                        {calc.name}
                      </td>
                      <td
                        className="table-td text-blue-600 truncate max-w-xs"
                        title={calc.custom_url}
                      >
                        {calc.custom_url}
                      </td>
                      {(canUpdate || canSimilar) && (
                        <td className="table-td text-center">
                          <div className="flex justify-center gap-2">
                            {canSimilar && (
                              <button
                                className="p-2 btn btn-primary"
                                onClick={() => {
                                  setSelectedCalcForSimilar(calc.id);
                                  setIsSimilarModalOpen(true);
                                }}
                                title="Similar Items"
                              >
                                <MdLink size={18} />
                                <span>Similar Items</span>
                              </button>
                            )}
                            {canUpdate && (
                              <button
                                className="p-2 btn btn-edit"
                                onClick={() => openModal(index)}
                                title="Edit"
                              >
                                {/* <MdEdit size={18} /> */}
                                <span>Edit</span>
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="p-4 border-t">
              <GlobalPagination
                offset={pagination.offset}
                limit={pagination.limit}
                total={pagination.totalCount}
                onPageChange={handlePageChange}
                className="bg-white"
              />
            </div>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-8 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative shadow-2xl">
            <XMarkIcon
              className="absolute top-4 right-4 h-6 w-6 text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
              onClick={closeModal}
            />
            <h3 className="text-2xl font-bold mb-6 text-center text-gray-800">
              {editIndex !== null ? "Edit Calculator" : "Add Calculator"}
            </h3>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Display Image
                </label>
                {formData.preview ? (
                  <div className="relative">
                    <div className="w-full max-w-xs mx-auto aspect-[853/1280] bg-gray-50 rounded-xl overflow-hidden border-2 border-gray-200">
                      <ImageWithFallback
                        src={formData.preview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removePreviewImage}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer w-full max-w-xs mx-auto aspect-[853/1280] bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center hover:bg-gray-100 transition-all"
                  >
                    <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
                    <span className="text-gray-600 font-medium">
                      Upload Image
                    </span>
                  </label>
                )}
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="calc-name"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Calculator Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="calc-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g. Brick Calculator"
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                />
              </div>

              {editIndex === null && (
                <div className="space-y-2">
                  <label
                    htmlFor="calc-url"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    Custom URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="calc-url"
                    type="text"
                    value={formData.custom_url}
                    onChange={(e) =>
                      setFormData({ ...formData, custom_url: e.target.value })
                    }
                    placeholder="e.g. /brick-calculator"
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className={`flex-1 px-4 py-3 rounded-xl font-semibold text-white transition-all ${
                    isFormValid()
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                  onClick={
                    editIndex !== null
                      ? handleUpdateCalculator
                      : handleAddCalculator
                  }
                  disabled={submitting || !isFormValid()}
                >
                  {submitting
                    ? "Saving..."
                    : editIndex !== null
                      ? "Update"
                      : "Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={() => {
            setIsSimilarModalOpen(false);
            setSelectedCalcForSimilar(null);
          }}
          sourceId={selectedCalcForSimilar}
          sourceType="construction_calculator"
        />
      )}
    </div>
  );
}

export default ConstructionCalculator;
